import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import { StatusBadge } from "@/design-system/StatusBadge";
import { BybitKeyGuide } from "@/features/identity/BybitKeyGuide";
import { connectBybitAccount, disconnectBybitAccount, getBybitConnection, type ConnectionResponse } from "@/shared/api";

const messages: Record<string, string> = {
  INVALID_CREDENTIALS: "Bybit отклонил ключ. Проверьте API Key и Secret.",
  INSUFFICIENT_PERMISSIONS: "Нужны разрешения Read + Trade для контрактов. Withdraw запрещён; внутренний перевод требует отдельного разрешения и политики.",
  WRONG_ACCOUNT_MODE: "Режим счёта не поддерживается: нужен Unified Trading Account с cross или isolated margin.",
  WRONG_IP_ALLOWLIST: "IP allowlist ключа не соответствует адресу подключения Skryptos.",
  DIFFERENT_ACCOUNT: "Ключ принадлежит другому счёту Bybit. Прежнее подключение сохранено.",
  BYBIT_UNAVAILABLE: "Bybit временно недоступен. Попробуйте ещё раз.",
  INVALID_RESPONSE: "Не удалось подтвердить сведения Bybit. Подключение не изменено.",
  SWITCH_CONFLICT: "Другой запрос уже обрабатывается. Дождитесь результата.",
  STORAGE_UNAVAILABLE: "Не удалось сохранить подключение. Прежний ключ остаётся активным.",
  // 2026-10-13: the first client typed the Bybit password here twice.
  REAUTHENTICATION_REQUIRED: "Пароль Skryptos не подошёл. Нужен пароль, с которым вы входите на этот сайт, — не пароль от Bybit.",
  ACCOUNT_ACCESS_DENIED: "Изменить подключение может владелец этого счёта.",
  TERMS_NOT_ACCEPTED: "Сначала примите условия использования — обновите страницу.",
  INVALID_INPUT: "Проверьте заполнение полей.",
};

const healthMessages: Record<string, string> = {
  PROVEN: "Доступ и сверка подтверждены свежими данными Bybit.",
  RECONCILING: "Сверка не завершена. Актуальное состояние ещё не подтверждено.",
  DEGRADED: "Свежая сверка недоступна. Биржевые действия ожидают подтверждения состояния.",
  CREDENTIAL_UNAVAILABLE: "Доступ к Bybit недоступен или срок ключа истёк. Проверьте ключ и разрешения.",
  EXCHANGE_UNKNOWN: "Ответ Bybit не подтверждён. Позиции и ордера требуют повторной сверки.",
  DISCONNECTED: "Действующее подключение не подтверждено.",
};

export function BybitConnectionPanel({ tenantId, accountId, roles }: { tenantId: string; accountId: string; roles: readonly string[] }): React.JSX.Element {
  const cache = useQueryClient();
  const queryKey = ["bybit-connection", tenantId, accountId];
  const [editing, setEditing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef<string | null>(null);
  const connection = useQuery({
    queryKey,
    queryFn: async (): Promise<ConnectionResponse> => {
      const { data, error } = await getBybitConnection({ credentials: "same-origin", cache: "no-store" });
      if (error !== undefined || data === undefined) throw new Error("connection unavailable");
      return data;
    },
    refetchInterval: (query) => query.state.data?.request_status === "PENDING" || query.state.data?.disconnect_status === "PENDING" ? 2000 : query.state.data?.status === "CONNECTED" ? 15000 : false,
  });
  const value = connection.data;
  const disconnectPending = value?.disconnect_status === "PENDING";
  const pending = submitting || value?.request_status === "PENDING" || disconnectPending;
  const canEdit = roles.includes("OWNER") && value?.status !== "LEGACY_CONNECTED";
  const shownError = error ?? (value?.request_status === "REJECTED" ? value.error ?? null : null);

  async function submit(event: React.SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const fields = new FormData(form);
    const field = (name: string): string => {
      const value = fields.get(name);
      return typeof value === "string" ? value : "";
    };
    // No secret in query/mutation caches, local/session storage or component state.
    const body = {
      request_id: requestId.current ??= crypto.randomUUID(), api_key: field("api_key"),
      api_secret: field("api_secret"),
      // Within two hours of signing in the sign-in is the confirmation.
      ...(field("password") === "" ? {} : { password: field("password") }),
    };
    form.reset();
    setSubmitting(true);
    setError(null);
    try {
      const result = await connectBybitAccount({ credentials: "same-origin", cache: "no-store", body });
      if (result.error !== undefined) {
        const failure: unknown = result.error;
        setError(typeof failure === "object" && failure !== null && "detail" in failure && typeof failure.detail === "string" ? failure.detail : "CONNECTION_UNAVAILABLE");
      } else {
        setEditing(false);
      }
    } catch {
      setError("CONNECTION_UNAVAILABLE");
    } finally {
      setSubmitting(false);
      await cache.invalidateQueries({ queryKey });
    }
  }

  async function disconnect(event: React.SyntheticEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const form = event.currentTarget;
    const typed = new FormData(form).get("password");
    form.reset();
    const password = typeof typed === "string" && typed !== "" ? typed : undefined;
    setSubmitting(true);
    setError(null);
    try {
      const result = await disconnectBybitAccount({ credentials: "same-origin", cache: "no-store", body: {
        request_id: requestId.current ??= crypto.randomUUID(), ...(password === undefined ? {} : { password }),
      } });
      if (result.error !== undefined) {
        const failure: unknown = result.error;
        setError(typeof failure === "object" && failure !== null && "detail" in failure && typeof failure.detail === "string" ? failure.detail : "CONNECTION_UNAVAILABLE");
      } else {
        setDisconnecting(false);
      }
    } catch {
      setError("CONNECTION_UNAVAILABLE");
    } finally {
      setSubmitting(false);
      await cache.invalidateQueries({ queryKey });
    }
  }

  return <Panel title="Подключение Bybit" testId="bybit-connection">
    <div className="flex flex-col gap-4">
      {connection.isPending && <p role="status">Загружаем подключение…</p>}
      {connection.isError && <p role="alert">Подключение сейчас недоступно. Повторите позже.</p>}
      {connection.isError && value !== undefined && <p>Последние известные сведения; актуальное состояние не подтверждено.</p>}
      {value?.status === "LEGACY_CONNECTED" && <p>Действующий торговый счёт подключён.</p>}
      {/* One line says what matters — connected, which account, reconciled
          or not, when last checked; the rest is one click away
          (owner, 2026-10-17: the technical text read as the main content). */}
      {value?.status === "CONNECTED" && <div className="flex flex-col gap-1">
        <p className="flex flex-wrap items-center gap-2 text-text-primary">
          <span>{connection.isError ? "Сохранённое подключение Bybit" : "Bybit подключён"} · {value.account_identifier}</span>
          {!connection.isError && <StatusBadge tone={value.execution_state === "PROVEN" ? "ready" : "warning"}>{value.execution_state === "PROVEN" ? "сверено" : "сверяется"}</StatusBadge>}
        </p>
        <p className="text-helper-sm text-text-muted">Последняя проверка: {value.checked_at == null ? "—" : new Date(value.checked_at).toLocaleString("ru-RU")}</p>
      </div>}
      {value !== undefined && value.status !== "DISCONNECTED" && value.admissions_paused === true && <p className="text-status-warning">Новые входы аккаунта на паузе.</p>}
      {value !== undefined && value.status !== "DISCONNECTED" && value.expiring_soon === true && <p role="status" className="text-status-warning">До истечения ключа осталось не более 7 дней. Подготовьте замену ключа.</p>}
      {value !== undefined && value.status !== "DISCONNECTED" && <details className="text-helper-sm">
        <summary className="cursor-pointer select-none rounded-row text-text-secondary focus-visible:outline-2 focus-visible:outline-focus-ring">Права ключа и сверка</summary>
        <div aria-live="polite" data-testid="connection-health" className="mt-2 flex flex-col gap-1 text-text-secondary">
          {value.status === "CONNECTED" && <p>При подключении проверены Read + Trade. Withdraw запрещён. Внутренние переводы доступны только при отдельном разрешении API и включённой политике автоматических переводов.</p>}
          {!connection.isError && <p>{healthMessages[value.execution_state ?? "RECONCILING"]}</p>}
          {!connection.isError && value.recovered_at != null && value.execution_state === "PROVEN" && <p>Доступ и сверка восстановлены. Режим новых входов не изменён.</p>}
          {value.expires_at != null ? <p>Срок действия ключа: {new Date(value.expires_at).toLocaleString("ru-RU")}</p> : <p>Срок действия ключа не подтверждён.</p>}
        </div>
      </details>}
      {pending && !disconnectPending && !disconnecting && <p role="status" aria-live="polite">Проверяем подключение… Прежний ключ остаётся активным до успешной проверки.</p>}
      {value?.status === "DISCONNECTED" && value.disconnect_status === "COMPLETED" && <p role="status">Bybit отключён. Новые входы остаются на паузе.</p>}
      {shownError !== null && <p role="alert">{messages[shownError] ?? "Не удалось завершить запрос. Обновите статус подключения перед повтором."}</p>}
      {disconnectPending && <p role="status">Новые входы приостановлены. Существующие циклы продолжают сопровождение. Отключение завершится после проверки отсутствия позиций, ордеров и незавершённых действий.</p>}
      {value?.disconnect_status === "CANCELLED" && <p role="status">Запрос отключения отменён: доступ или подключение изменились. Новые входы остаются на паузе.</p>}
      {canEdit && value !== undefined && !pending && !editing && !disconnecting && <div className="flex flex-wrap gap-2">
        <Button variant={value.status === "CONNECTED" ? "secondary" : "primary"} onClick={() => { requestId.current = crypto.randomUUID(); setEditing(true); setError(null); }}>
          {value.status === "CONNECTED" ? "Заменить ключ" : "Подключить Bybit"}
        </Button>
        {value.status === "CONNECTED" && <Button variant="ghost" onClick={() => { requestId.current = crypto.randomUUID(); setDisconnecting(true); setError(null); }}>Отключить Bybit</Button>}
      </div>}
      {canEdit && disconnecting && !pending && <form onSubmit={(event) => { void disconnect(event); }} className="flex flex-col gap-3" autoComplete="off">
        <p>Новые входы будут приостановлены. Skryptos сохранит подключение для сопровождения существующих циклов до безопасного завершения. Позиции не закрываются ради отключения.</p>
        {value?.confirmation_required === false
          ? <p className="text-helper-sm text-text-muted">Вы недавно вошли — пароль вводить не нужно.</p>
          : <label>Пароль Skryptos<input name="password" type="password" required maxLength={1024} autoComplete="current-password" className="mt-1 block w-full rounded border border-border-subtle bg-background-base p-2" /></label>}
        <div className="flex gap-3"><Button type="submit">Приостановить входы и отключить</Button><Button type="button" onClick={() => { setDisconnecting(false); }}>Отмена</Button></div>
      </form>}
      {canEdit && editing && !pending && <form onSubmit={(event) => { void submit(event); }} className="flex flex-col gap-3" autoComplete="off">
        <BybitKeyGuide requiredIp={value?.required_ip} />
        <label>API Key<input name="api_key" type="password" required maxLength={512} autoComplete="off" className="mt-1 block w-full rounded border border-border-subtle bg-background-base p-2" /></label>
        <label>API Secret<input name="api_secret" type="password" required maxLength={512} autoComplete="new-password" className="mt-1 block w-full rounded border border-border-subtle bg-background-base p-2" /></label>
        {value?.confirmation_required === false ? (
          <p className="text-helper-sm text-text-muted" data-testid="bybit-recent-sign-in">Вы недавно вошли — пароль вводить не нужно.</p>
        ) : (
          <>
            <label>Пароль Skryptos<input name="password" type="password" required maxLength={1024} autoComplete="current-password" aria-describedby="skryptos-password-hint" className="mt-1 block w-full rounded border border-border-subtle bg-background-base p-2" /></label>
            <p id="skryptos-password-hint" className="-mt-2 text-helper-sm text-text-muted">Тот, с которым вы входите на этот сайт. Пароль от Bybit не нужен — мы его никогда не спрашиваем.</p>
          </>
        )}
        <p className="text-helper-sm text-text-muted">Подключение не запускает торговлю. Стратегия и запуск настраиваются отдельно.</p>
        <div className="flex gap-3"><Button type="submit" variant="primary">Проверить и подключить</Button><Button type="button" onClick={() => { setEditing(false); }}>Отмена</Button></div>
      </form>}
    </div>
  </Panel>;
}
